/**
 * DataSourcePicker — V2 replacement for the legacy `DataSources.js` panel. A
 * popover with tabs: "Sources" (toggle chips + inline id pickers), plus "Files"
 * / "Layers" / "Seerist" tabs that wrap the existing selectors. The selection
 * shape is unchanged (see `dataSourceSelections.ts`).
 */
import React, { Suspense, useCallback, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { toast } from 'react-toastify';
import {
  CheckIcon,
  DatabaseIcon,
  PencilIcon,
  PlusIcon,
  SettingsIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { observability } from 'utils/observability';

import {
  useCreateCustomDataSource,
  useDeleteCustomDataSource,
  useUpdateCustomDataSource,
} from '../hooks/useCustomDataSources';
import {
  useDataSourceSelections,
  type SelectOption,
} from './hooks/useDataSourceSelections';
import type { ConfigTab, DataSourceOption } from './dataSourceSelections';
import type { RealtimeSelection } from './types';

type PickerTab = 'sources' | 'files' | 'layers' | 'seerist' | 'web';

// The per-tab editors are heavy legacy components (the layer selector alone
// reaches the ArcGIS map context); load them only when their tab is shown.
const ChatFileSelector = React.lazy(() => import('../ChatFileSelector'));
const ChatLayerSelector = React.lazy(() => import('../ChatLayerSelector'));
const SeeristRequestBuilder = React.lazy(
  () => import('../SeeristRequestBuilder')
);
const CustomDataSourceEditDrawer = React.lazy(
  () => import('../CustomDataSourceEditDrawer')
);
const WebDateRangePanel = React.lazy(() => import('./WebDateRangePanel'));

const TabFallback: React.FC = () => (
  <div className="tw:flex tw:flex-col tw:gap-2 tw:p-2" aria-busy="true">
    <Skeleton className="tw:h-8 tw:w-full" />
    <Skeleton className="tw:h-8 tw:w-5/6" />
    <Skeleton className="tw:h-8 tw:w-2/3" />
  </div>
);

export interface DataSourcePickerProps {
  selections: RealtimeSelection[];
  setSelections: React.Dispatch<React.SetStateAction<RealtimeSelection[]>>;
  disabled?: boolean;
}

/** Sources that pick a single id from a list, rendered inline as a select. */
const ID_PICKER_SOURCES: Record<
  string,
  { placeholder: string; optionsKey: keyof OptionSets }
> = {
  SITREP: { placeholder: 'Choose a PRATUS AI SITREP…', optionsKey: 'sitrep' },
  'NEO Analysis': { placeholder: 'Choose a NEO Analysis…', optionsKey: 'neo' },
  Incident: { placeholder: 'Choose an Incident…', optionsKey: 'incident' },
  Exercise: { placeholder: 'Choose an Exercise…', optionsKey: 'exercise' },
};

interface OptionSets {
  sitrep: SelectOption[];
  neo: SelectOption[];
  incident: SelectOption[];
  exercise: SelectOption[];
}

interface SourceChipProps {
  source: DataSourceOption;
  label: string;
  selected: boolean;
  needsConfig: boolean;
  onToggle: () => void;
  onConfigure?: () => void;
  onRemove?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const SourceChip: React.FC<SourceChipProps> = ({
  source,
  label,
  selected,
  needsConfig,
  onToggle,
  onConfigure,
  onRemove,
  onEdit,
  onDelete,
}) => {
  const aorText = useAorText();
  return (
    <div
      className={cn(
        'tw:group/chip tw:inline-flex tw:max-w-full tw:items-center tw:gap-0.5 tw:rounded-full tw:border tw:pr-0.5 tw:text-xs',
        selected
          ? 'tw:border-primary/60 tw:bg-primary/10 tw:text-foreground'
          : 'tw:border-border tw:bg-background tw:text-foreground tw:hover:bg-accent'
      )}
      data-selected={selected || undefined}
    >
      <button
        type="button"
        className="tw:inline-flex tw:min-w-0 tw:cursor-pointer tw:items-center tw:gap-1 tw:rounded-full tw:border-0 tw:bg-transparent tw:py-1 tw:pl-2.5 tw:pr-1.5 tw:text-left tw:text-inherit"
        aria-pressed={selected}
        aria-label={`${selected ? 'Deselect' : 'Select'} ${source.source}`}
        title={source.description ? aorText(source.description) : source.source}
        onClick={selected && onConfigure ? onConfigure : onToggle}
      >
        {selected && <CheckIcon className="tw:size-3 tw:shrink-0" />}
        <span className="tw:truncate">{label}</span>
        {needsConfig && (
          <span
            className="tw:ml-0.5 tw:size-1.5 tw:shrink-0 tw:rounded-full tw:bg-destructive"
            aria-label="Needs configuration"
          />
        )}
      </button>
      {selected && onConfigure && (
        <Button
          variant="ghost"
          size="icon-xs"
          className="tw:size-5 tw:rounded-full"
          aria-label={`Configure ${source.source}`}
          onClick={onConfigure}
        >
          <SettingsIcon />
        </Button>
      )}
      {!selected && onEdit && (
        <Button
          variant="ghost"
          size="icon-xs"
          className="tw:size-5 tw:rounded-full"
          aria-label={`Edit ${source.source}`}
          onClick={onEdit}
        >
          <PencilIcon />
        </Button>
      )}
      {!selected && onDelete && (
        <Button
          variant="ghost"
          size="icon-xs"
          className="tw:size-5 tw:rounded-full"
          aria-label={`Delete ${source.source}`}
          onClick={onDelete}
        >
          <Trash2Icon />
        </Button>
      )}
      {selected && onRemove && (
        <Button
          variant="ghost"
          size="icon-xs"
          className="tw:size-5 tw:rounded-full"
          aria-label={`Remove ${source.source}`}
          onClick={onRemove}
        >
          <XIcon />
        </Button>
      )}
    </div>
  );
};

const DataSourcePicker: React.FC<DataSourcePickerProps> = ({
  selections,
  setSelections,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<PickerTab>('sources');
  const [search, setSearch] = useState('');
  const [idPickerFor, setIdPickerFor] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{
    open: boolean;
    source: Record<string, unknown> | null;
  }>({ open: false, source: null });
  const [deleteCustomId, setDeleteCustomId] = useState<string | null>(null);

  const ds = useDataSourceSelections({
    selections,
    setSelections,
    searchTerm: search,
  });

  const createCustom = useCreateCustomDataSource();
  const updateCustom = useUpdateCustomDataSource();
  const deleteCustom = useDeleteCustomDataSource();

  const optionSets: OptionSets = {
    sitrep: ds.sitrepOptions,
    neo: ds.neoOptions,
    incident: ds.incidentOptions,
    exercise: ds.exerciseOptions,
  };

  const openConfigTab = useCallback((t: ConfigTab) => {
    if (t) setTab(t);
  }, []);

  const handleToggle = (source: DataSourceOption) => {
    const wasSelected = ds.isSelected(source.source);
    const openTab = ds.toggle(source);
    if (!wasSelected && ID_PICKER_SOURCES[source.source]) {
      // Open the inline id picker unless the toggle already auto-filled it
      // (current incident/exercise).
      setIdPickerFor(source.source);
    }
    openConfigTab(openTab);
  };

  const handleConfigure = (source: DataSourceOption) => {
    if (source.source === 'Files') setTab('files');
    else if (source.source === 'Layers') setTab('layers');
    else if (source.source === 'Seerist') setTab('seerist');
    else if (source.source === 'Web') setTab('web');
    else if (ID_PICKER_SOURCES[source.source]) setIdPickerFor(source.source);
  };

  const handleSaveCustom = (data: Record<string, unknown>) => {
    const mutation = drawer.source ? updateCustom : createCustom;
    const verb = drawer.source ? 'updated' : 'created';
    mutation.mutate(data as never, {
      onSuccess: () => {
        toast.success(`Custom data source ${verb} successfully.`);
        setDrawer({ open: false, source: null });
      },
      onError: (error: unknown) => {
        observability.captureException(error, {
          where: 'chatV2.DataSourcePicker.saveCustom',
        });
        toast.error(
          `Failed to ${drawer.source ? 'update' : 'create'} custom data source.`
        );
      },
    });
  };

  const handleConfirmDeleteCustom = () => {
    if (!deleteCustomId) return;
    deleteCustom.mutate(deleteCustomId as never, {
      onSuccess: () =>
        toast.success('Custom data source deleted successfully.'),
      onError: (error: unknown) => {
        observability.captureException(error, {
          where: 'chatV2.DataSourcePicker.deleteCustom',
        });
        toast.error('Failed to delete custom data source.');
      },
      onSettled: () => setDeleteCustomId(null),
    });
  };

  const triggerLabel =
    ds.count > 0
      ? `${ds.count} source${ds.count === 1 ? '' : 's'}`
      : 'Data sources';

  const showFiles = ds.isSelected('Files');
  const showLayers = ds.isSelected('Layers');
  const showSeerist = ds.isSelected('Seerist');
  const showWeb = ds.isSelected('Web');
  const activeTab: PickerTab =
    (tab === 'files' && !showFiles) ||
    (tab === 'layers' && !showLayers) ||
    (tab === 'seerist' && !showSeerist) ||
    (tab === 'web' && !showWeb)
      ? 'sources'
      : tab;

  const renderIdPicker = (sourceName: string) => {
    const cfg = ID_PICKER_SOURCES[sourceName];
    if (!cfg) return null;
    const options = optionSets[cfg.optionsKey];
    const current = selections.find((s) => s.source === sourceName)?.id ?? null;
    return (
      <div
        className="tw:flex tw:items-center tw:gap-2"
        key={`picker-${sourceName}`}
      >
        <span className="tw:shrink-0 tw:text-xs tw:text-muted-foreground">
          {sourceName}
        </span>
        {options.length === 0 ? (
          <span className="tw:text-xs tw:text-muted-foreground">
            None available
          </span>
        ) : (
          <Select
            value={current || null}
            onValueChange={(next) => {
              ds.setId(sourceName, next ? String(next) : null);
              setIdPickerFor(null);
            }}
          >
            <SelectTrigger size="sm" aria-label={cfg.placeholder}>
              <SelectValue placeholder={cfg.placeholder}>
                {options.find((o) => o.value === current)?.label ??
                  cfg.placeholder}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="dark">
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="Close picker"
          onClick={() => setIdPickerFor(null)}
        >
          <XIcon />
        </Button>
      </div>
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              variant={ds.count > 0 ? 'secondary' : 'outline'}
              size="sm"
              disabled={disabled}
              aria-label="Data sources"
              data-testid="ai-data-sources"
            />
          }
        >
          <DatabaseIcon />
          <span data-testid="ai-data-sources-count">{triggerLabel}</span>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="top"
          className="dark tw:w-[min(32rem,calc(100vw-2rem))] tw:gap-2 tw:p-2"
          aria-label="Data sources"
        >
          <Tabs
            value={activeTab}
            onValueChange={(v) => setTab(v as PickerTab)}
            className="tw:gap-2"
          >
            <TabsList variant="line" className="tw:w-full tw:justify-start">
              <TabsTrigger value="sources">Sources</TabsTrigger>
              {showFiles && <TabsTrigger value="files">Files</TabsTrigger>}
              {showLayers && <TabsTrigger value="layers">Layers</TabsTrigger>}
              {showSeerist && (
                <TabsTrigger value="seerist">Seerist</TabsTrigger>
              )}
              {showWeb && <TabsTrigger value="web">Web</TabsTrigger>}
            </TabsList>

            <TabsContent
              value="sources"
              className="tw:flex tw:flex-col tw:gap-2"
            >
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sources..."
                aria-label="Search sources"
              />
              <ScrollArea className="tw:max-h-80">
                <div className="tw:flex tw:flex-col tw:gap-3 tw:p-0.5">
                  {ds.selectedSources.length > 0 && (
                    <section
                      aria-label="Active sources"
                      className="tw:flex tw:flex-col tw:gap-1.5"
                    >
                      <h4 className="tw:m-0 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                        Active
                      </h4>
                      <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                        {ds.selectedSources.map((source) => (
                          <SourceChip
                            key={`active-${source.source}`}
                            source={source}
                            label={ds.displayName(source)}
                            selected
                            needsConfig={ds.needsConfig(source)}
                            onToggle={() => ds.remove(source.source)}
                            onConfigure={
                              source.source in ID_PICKER_SOURCES ||
                              ['Files', 'Layers', 'Seerist', 'Web'].includes(
                                source.source
                              )
                                ? () => handleConfigure(source)
                                : undefined
                            }
                            onRemove={() => ds.remove(source.source)}
                          />
                        ))}
                      </div>
                      {idPickerFor && renderIdPicker(idPickerFor)}
                    </section>
                  )}

                  <section
                    aria-label="Standard sources"
                    className="tw:flex tw:flex-col tw:gap-1.5"
                  >
                    <h4 className="tw:m-0 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                      Standard
                    </h4>
                    <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                      {ds.standardSources
                        .filter((s) => !ds.isSelected(s.source))
                        .map((source) => (
                          <SourceChip
                            key={source.source}
                            source={source}
                            label={ds.displayName(source)}
                            selected={false}
                            needsConfig={false}
                            onToggle={() => handleToggle(source)}
                          />
                        ))}
                    </div>
                    {ds.standardSources.filter((s) => !ds.isSelected(s.source))
                      .length === 0 && (
                      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                        {search
                          ? `No sources matching "${search}"`
                          : 'All sources selected'}
                      </p>
                    )}
                  </section>

                  <section
                    aria-label="Custom sources"
                    className="tw:flex tw:flex-col tw:gap-1.5"
                  >
                    <div className="tw:flex tw:items-center tw:justify-between">
                      <h4 className="tw:m-0 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                        Custom
                      </h4>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => setDrawer({ open: true, source: null })}
                        aria-label="Add custom data source"
                      >
                        <PlusIcon /> Add
                      </Button>
                    </div>
                    <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                      {ds.customSources
                        .filter((s) => !ds.isSelected(s.source))
                        .map((source) => (
                          <SourceChip
                            key={`custom-${source.id}`}
                            source={source}
                            label={ds.displayName(source)}
                            selected={false}
                            needsConfig={false}
                            onToggle={() => handleToggle(source)}
                            onEdit={() =>
                              setDrawer({
                                open: true,
                                source: source.customData ?? null,
                              })
                            }
                            onDelete={() =>
                              setDeleteCustomId(
                                String(source.customData?.id ?? source.id)
                              )
                            }
                          />
                        ))}
                    </div>
                    {ds.customSources.length === 0 && (
                      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                        {search
                          ? 'No custom sources match'
                          : 'No custom sources yet'}
                      </p>
                    )}
                  </section>
                </div>
              </ScrollArea>
            </TabsContent>

            {showFiles && (
              <TabsContent
                value="files"
                className="tw:max-h-[60vh] tw:overflow-y-auto"
              >
                <Suspense fallback={<TabFallback />}>
                  <ChatFileSelector
                    workspaceId={ds.workspaceId}
                    selectedFileIds={ds.fileIds}
                    onFileSelectionChange={ds.setFileIds}
                  />
                </Suspense>
              </TabsContent>
            )}
            {showLayers && (
              <TabsContent
                value="layers"
                className="tw:max-h-[60vh] tw:overflow-y-auto tw:p-1"
              >
                <Suspense fallback={<TabFallback />}>
                  <ChatLayerSelector
                    selectedLayers={ds.layers}
                    onChange={ds.setLayers}
                    filterByDatetime={ds.layersFilterByDatetime}
                    onFilterByDatetimeChange={
                      ds.setLayersFilterByDatetimeEnabled
                    }
                  />
                </Suspense>
              </TabsContent>
            )}
            {showSeerist && (
              <TabsContent
                value="seerist"
                className="tw:max-h-[60vh] tw:overflow-y-auto tw:p-1"
              >
                <Suspense fallback={<TabFallback />}>
                  <SeeristRequestBuilder
                    value={ds.seeristRequest as never}
                    onChange={(request) => ds.setSeerist(request as never)}
                  />
                </Suspense>
              </TabsContent>
            )}
            {showWeb && (
              <TabsContent
                value="web"
                className="tw:max-h-[60vh] tw:overflow-y-auto tw:p-1"
              >
                <Suspense fallback={<TabFallback />}>
                  <WebDateRangePanel
                    value={ds.webDateRange}
                    onChange={ds.setWebDateRange}
                  />
                </Suspense>
              </TabsContent>
            )}
          </Tabs>
        </PopoverContent>
      </Popover>

      {drawer.open && (
        <Suspense fallback={null}>
          <CustomDataSourceEditDrawer
            onHide={() => setDrawer({ open: false, source: null })}
            onSave={handleSaveCustom}
            dataSource={drawer.source}
          />
        </Suspense>
      )}

      <AlertDialog
        open={!!deleteCustomId}
        onOpenChange={(o) => !o && setDeleteCustomId(null)}
      >
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Data Source</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this Custom Data Source?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleConfirmDeleteCustom}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DataSourcePicker;
